import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const images = [
  {
    src: "https://images.unsplash.com/photo-1722515177905-930b3e2bcea6?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "exterior",
  },
  {
    src: "https://images.unsplash.com/photo-1709187516056-d4929b67e89f?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "rooms",
  },
  {
    src: "https://images.unsplash.com/photo-1736618625396-571234ade4d7?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "pool",
  },
  {
    src: "https://images.unsplash.com/photo-1612038792440-afb231b6ce15?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "dining",
  },
  {
    src: "https://images.unsplash.com/photo-1721369483526-62f48a00b949?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "rooms",
  },
  {
    src: "https://images.unsplash.com/photo-1562371589-d02ea4e9754e?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "exterior",
  },
  {
    src: "https://images.unsplash.com/photo-1636714507452-48716cfa1818?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "spa",
  },
  {
    src: "https://images.unsplash.com/photo-1762328823694-553f08e46f09?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "spa",
  },
];

const categories = [
  { id: "all", label: "All" },
  { id: "exterior", label: "Exterior" },
  { id: "rooms", label: "Rooms" },
  { id: "pool", label: "Pool" },
  { id: "dining", label: "Dining" },
  { id: "spa", label: "Spa" },
];

export const Gallery = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages =
    activeCategory === "all"
      ? images
      : images.filter((img) => img.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="gallery-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1562371589-d02ea4e9754e?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Gallery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0F1F3C]/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl lg:text-6xl text-white">{t('nav.gallery')}</h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300 ${
                  activeCategory === cat.id
                    ? "bg-[#0F1F3C] text-white"
                    : "bg-white text-[#0F1F3C] hover:bg-[#0F1F3C]/5"
                }`}
                data-testid={`gallery-filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`cursor-pointer overflow-hidden ${
                  index % 5 === 0 ? "col-span-2 row-span-2" : ""
                }`}
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.src}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  style={{ aspectRatio: index % 5 === 0 ? "1" : "4/3" }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
        >
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            src={selectedImage.src}
            alt="Gallery"
            className="max-w-full max-h-full object-contain"
          />
          <button
            className="absolute top-6 right-6 text-white hover:text-[#D4AF37] transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <span className="text-2xl">×</span>
          </button>
        </div>
      )}
    </div>
  );
};
